<!--
  - Copyright (c) 2022. Haodong JU
  -->

<template>
  <v-card
    class="py-4 pl-2"
    elevation="1"
    rounded
  >
    <div class="d-flex align-center">
      <v-text-field
        v-model="search"
        :label="$t('search')"
        class="mr-1"
        clearable
        dense
        hide-details
        outlined
        prepend-inner-icon="mdi-magnify"
        single-line
      />
      <v-select
        v-model="activeCategoryId"
        :items="categoryList"
        :menu-props="{offsetY:true}"
        :placeholder="$t('selectGoodClassification')"
        class="mr-1"
        dense
        hide-details
        item-text="_langsname"
        item-value="id"
        multiple
        outlined
      >
        <template
          #selection="{ item, index }"
        >
          <span
            v-if="index==0"
            class="grey--text text-caption"
          >
            {{ $t('selectGoodClassification') }}(+{{ activeCategoryId.length || 1 }})
          </span>
        </template>
      </v-select>

      <v-select
        v-model="activeCategoryTypeId"
        :items="categoryTypeList"
        :menu-props="{offsetY:true}"
        :placeholder="$t('selectTaxType')"
        class="mr-1"
        dense
        hide-details
        item-text="name"
        item-value="id"
        multiple
        outlined
      >
        <template
          #selection="{ item, index }"
        >
          <span
            v-if="index==0"
            class="grey--text text-caption"
          >
            {{ $t('selectTaxType') }}(+{{ activeCategoryTypeId.length || 1 }})
          </span>
        </template>
      </v-select>

      <v-btn
        :color="showGraph?'primary':'grey'"
        class="mr-1"
        elevation="0"
        outlined
        width="160px"
        @click="changeShowGraph"
      >
        <v-icon left>
          <template v-if="showGraph">
            mdi-checkbox-marked
          </template>
          <template v-else>
            mdi-checkbox-blank-outline
          </template>
        </v-icon>
        <div
          class="hideMore"
          style="max-width: 90px"
        >
          <div class="text-caption">
            {{ $t('showSalesChange') }}
          </div>
        </div>
      </v-btn>
      <v-btn
        class="mr-1"
        color="primary"
        elevation="0"
        outlined
        width="160px"
      >
        <div class="text-caption">
          {{ $t('totalSales') }}：{{ totalCount }}
        </div>
      </v-btn>
      <v-btn
        class="mr-1"
        color="green"
        elevation="0"
        width="110px"
        @click="exportExcel"
      >
        <div class="text-caption">
          {{ $t('excelExport') }}
        </div>
      </v-btn>
    </div>

    <div
      v-if="atLeastOne.length>0"
      :style="showGraph?{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gridGap:'8px'}:{display: 'grid',gridTemplateColumns: '100%',gridGap: '4px'}"
      class="mt-4"
      style="display: grid;grid-template-columns: repeat(4,1fr);grid-gap: 8px"
    >
      <template v-for="item in atLeastOne">
        <v-card
          :key="item.code"
          :height="showGraph?'196px':'auto'"
          color="#f0f0f0"
          elevation="0"
        >
          <div class="d-flex align-center pa-2">
            <div>
              {{ item.name }}
            </div>
            <v-spacer/>
            <v-chip
              class="mt-1"
              label
            >
              × {{ item.totalCount }}
            </v-chip>
          </div>

          <div
            v-if="showGraph&&item.chartData[0].data.length>1"
            style="position:absolute;bottom:0;left:0"
          >
            <apexchart
              :options="item.chartOptions"
              :series="item.chartData"
              height="80px"
              type="area"
              width="100%"
            />
          </div>
        </v-card>
      </template>
    </div>
    <div
      v-else
      class="mt-4 pa-1"
    >
      {{ $t('noDataNow') }}
    </div>
  </v-card>
</template>

<script>
  import Category from '@/model/dish/Category'
  import CategoryType from '@/model/dish/CategoryType'
  import dayjs from 'dayjs'
  import DishStatistics from '@/model/statistic/DishStatistics'
  import { emptyOrIncludes, showSuccessMessage, wordContainsIn } from '@/common/utils'
  import { Remember } from '@/GlobalSettings'
  import { sortBy, uniq } from 'lodash-es'
  import IKUtils from 'innerken-js-utils'

  export default {
    name: 'DishStatisticFragment',

    props: {
      salesData: { type: Array },
    },
    data () {
      return {
        search: '',
        showGraph: Remember.showGraph,
        categoryList: [],
        categoryTypeList: [],
        model: DishStatistics,
        filterItem: {},
        activeCategoryId: [],
        activeCategoryTypeId: [],
      }
    },
    computed: {
      atLeastOne () {
        const displayData = this.salesData.filter(
          it => it.totalCount > 0 &&
            emptyOrIncludes(this.activeCategoryId, parseInt(it.categoryId)) &&
            emptyOrIncludes(this.activeCategoryTypeId, parseInt(it.categoryTypeId)) &&
            (wordContainsIn(this.search, it.code) || wordContainsIn(this.search, it.name)),
        )
        const allCategories = sortBy(uniq(displayData
          .map(it => it.detail.result.map(d => {
            return this.parseLabel(it.detail.smallSelector, d.smallSelector)
          }) ?? []).flat()))
        return displayData.map(
          it => {
            const data = allCategories.map(c => {
              return it.detail.result.find(th => this.parseLabel(it.detail.smallSelector, th.smallSelector) === c)?.spanTotal ?? 0
            })
            it.chartOptions =
              {
                colors: ['#66bb6a'],
                plotOptions: {
                  area: {
                    fillTo: 'end',
                  },
                },
                chart: {
                  sparkline: {
                    enabled: true,
                  },
                },
                xaxis: {
                  categories: allCategories,
                },
              }

            it.chartData =
              [{
                name: 'salesCount',
                data: data,
              }]

            return it
          },
        )
      },
      totalCount () {
        return this.atLeastOne.reduce((sum, i) => sum + parseInt(i.totalCount), 0)
      },
    },
    watch: {
      salesData () {
        this.activeCategoryTypeId = []
        this.activeCategoryId = []
      },
      showGraph (val) {
        Remember.showGraph = val
      },
    },
    async mounted () {
      this.categoryList = await Category.getList()
      this.categoryTypeList = await CategoryType.getList()
    },
    methods: {
      changeShowGraph () {
        IKUtils.showLoading()
        setTimeout(() => {
          this.showGraph = !this.showGraph
          this.$nextTick(() => {
            showSuccessMessage()
          })
        }, 300)
      },
      exportExcel () {
        const displayData = this.salesData.filter(it => it.totalCount > 0 &&
          emptyOrIncludes(this.activeCategoryId, parseInt(it.categoryId)) &&
          emptyOrIncludes(this.activeCategoryTypeId, parseInt(it.categoryTypeId)) &&
          (wordContainsIn(this.search, it.code) || wordContainsIn(this.search, it.name)),
        )
        // 数据对齐
        const allCategories = sortBy(uniq(displayData
          .map(it => it.detail.result.map(d => {
            return this.parseLabel(it.detail.smallSelector, d.smallSelector)
          }) ?? []).flat()))
        const res = displayData.map(
          it => {
            const data = allCategories.map(c => {
              return it.detail.result.find(th => this.parseLabel(it.detail.smallSelector, th.smallSelector) === c)?.spanTotal ?? '0.00'
            })
            it.monTurnover = data
            const numberData = data.map(Number)
            it.totalTurnover = numberData.reduce((x, y) => x + y)
            return it
          },
        )
        allCategories.unshift('商品名')
        allCategories.push('总计')

        const result = res.map(item => {
          return {
            name: item.name,
            totalTurnover: item.totalTurnover,
            monTurnover: item.monTurnover,
          }
        })
        const list = result.map(items => [items.name, ...items.monTurnover, items.totalTurnover])
        // 直接导出文件
        const ExportJsonExcel = require('js-export-excel')

        const option = {}

        option.fileName = '商品销售统计-' + dayjs().toISOString()

        option.datas = [
          {
            sheetData: list,
            sheetName: 'sheet',
            sheetHeader: allCategories,
            columnWidths: [20],
          },
        ]

        const toExcel = new ExportJsonExcel(option) // new
        toExcel.saveExcel() // 保存
      },
      parseLabel (dataType, data) {
        let res
        switch (dataType) {
          case 'hour':
            res = data + ':00'
            break
          case 'week':
            res = this.$i18n.tc('no_week', data)
            break
          case 'date':
            res = dayjs(data).format('DD.MM')
            break
          default:
          case 'month':
            res = dayjs().set('month', data - 1).format('MMM')
        }
        return res
      },
    },
  }
</script>

<style scoped>
.v-input {
  background: transparent !important;
}

</style>
